/* eslint-disable parog-web/migrate-non-patch-forms */
import { service } from '@ember/service';
import { validateNumber } from 'ember-changeset-validations/validators';
import type { IntlService } from 'ember-intl';
import JournalEventBasedComponent from 'parog-web/components/forc/abstract/journal-event-based-component';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import BioExamExpectedDosageChangedJournalEvent from 'parog-web/models/bio-exam-expected-dosage-changed-journal-event';
import type BioExamOrder from 'parog-web/models/bio-exam-order';

/**
 * @typedef {import("core/models/bio-exam-order").default} BioExamOrder
 */
import Provider from 'parog-web/components/forc/provider';

export default class BioExamMonitoringEditionForm extends JournalEventBasedComponent {
  static override forcDisplayName = 'Dosages immunosuppresseurs cibles (T0)';
  static override fullComponentId = 'bio-exam-monitoring/edition-form';
  static override journalEventClass = BioExamExpectedDosageChangedJournalEvent;

  @service declare intl: IntlService;

  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.lastJournalEvent;

  get bioExams(): BioExamOrder[] {
    return [
      // Tacrolimus
      '77348-1',
      // Cyclosporine
      '53828-0',
      // Sirolimus
      '72676-0',
      // Everolimus
      '72671-1'
    ].map(
      code => this.store.peekRecord('bio-exam-order', code) as BioExamOrder
    );
  }

  override get validations() {
    return this._generateValidation();
  }

  _computeMaxKey(code) {
    return `max-${code}`;
  }

  _computeMinKey(code) {
    return `min-${code}`;
  }

  _generateValidation() {
    const validations = {};

    this.bioExams.forEach(exam => {
      if (exam) {
        validations[this._computeMinKey(`LOINC-${exam.loinc}`)] =
          validateNumber({
            description: 'Le dosage minimum',
            allowBlank: true
          });
        validations[this._computeMaxKey(`LOINC-${exam.loinc}`)] =
          validateNumber({
            description: 'Le dosage maximum',
            allowBlank: true
          });
      }
    });

    return validations;
  }

  /**
   * @typedef {Object} ExpectedDosage
   * @property {number} min
   * @property {number} max
   * @property {string} code
   */
  override computePayload() {
    // Retrieve the dosages from the model
    const expectedDosages = this.bioExams
      .map(exam => {
        const min =
          this.model[this._computeMinKey(`LOINC-${exam.loinc}`)] || undefined;
        const max =
          this.model[this._computeMaxKey(`LOINC-${exam.loinc}`)] || undefined;

        return { code: `LOINC-${exam.loinc}`, min, max };
      })
      .filter(dosage => dosage.min !== undefined || dosage.max !== undefined);

    return {
      patient: this.patient,
      occurredAt: new Date(),
      expectedDosages
    };
  }

  override retrieveModel() {
    const model = {};

    // Inject values from the dosages directly into the model
    const expectedDosageJe =
      this.retrieveJournalEvent() as BioExamExpectedDosageChangedJournalEvent;
    if (expectedDosageJe) {
      expectedDosageJe.expectedDosages.forEach(dosage => {
        model[this._computeMinKey(dosage.code)] = dosage.min;
        model[this._computeMaxKey(dosage.code)] = dosage.max;
      });
    }

    return model;
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        {{#each this.bioExams as |bioExam|}}
          <h4>{{bioExam.longLabel}}</h4>
          <div
            class='fields inline'
            data-test-bio-exam-expected-dosage='LOINC-{{bioExam.loinc}}'
          >
            Entre
            <div class='three wide field'>
              <Form.NumberInput @propertyName='min-LOINC-{{bioExam.loinc}}' />
            </div>
            et
            <div class='three wide field'>
              <Form.NumberInput @propertyName='max-LOINC-{{bioExam.loinc}}' />
            </div>
            {{bioExam.unit}}
          </div>
        {{/each}}
      </:content>
    </Provider>
  </template>
}
